import { useId } from 'react';

import { Icons } from '@/assets/icons/icons';
import { cn } from '@/lib/utils';

import { Avatar } from '../../internals/Avatar';
import { ICON_SM_CLASS, PILL_CLASS } from '../../tokens';

import type { ChipChoice } from '../../internals/chipEntries';

export type ChipLook = 'chip' | 'pill' | 'text';

// chip: the recipient chip of a To or Guests row. pill: a property pill, as a
// label list draws it. text: a name inside a sentence, for the inline row
const LOOK_CLASS: Record<ChipLook, string> = {
  chip: 'h-[var(--ac-pill-h)] gap-[calc(6*var(--ac-u))] rounded-md bg-secondary pl-[calc(4*var(--ac-u))] pr-[calc(6*var(--ac-u))] text-[length:var(--ac-pill)] font-medium leading-[var(--ac-pill-lh)] text-foreground',
  pill: PILL_CLASS,
  text: 'relative h-[calc(20*var(--ac-u))] gap-[var(--ac-gap-xs)] text-[length:var(--ac-meta)] font-medium leading-[var(--ac-meta-lh)] text-foreground',
};

// A name inside a sentence keeps no gap for its remove button, which appears over the
// space after the name
const TEXT_REMOVE_CLASS = 'absolute left-full top-1/2 -translate-y-1/2';
// Half the remove button and gap centres the content while the button is transparent
const PILL_CONTENT_CLASS =
  'inline-flex min-w-0 translate-x-[calc(11*var(--ac-u))] items-center gap-[calc(6*var(--ac-u))] transition-transform duration-[120ms] group-hover/chip:translate-x-0 group-focus-within/chip:translate-x-0 motion-reduce:duration-[60ms]';

// A chip added during this mount scales in; chips already there render still
const ADDED_CLASS =
  'motion-scale-in-[0.92] motion-opacity-in-0 motion-duration-[120ms] motion-ease-out motion-reduce:motion-duration-[60ms]';

const INVALID_CLASS = 'border border-danger-8 text-danger-11';

const REMOVE_CLASS =
  'inline-flex size-[calc(16*var(--ac-u))] shrink-0 items-center justify-center overflow-hidden rounded-sm text-foreground-muted opacity-0 transition-opacity duration-[120ms] hover:text-foreground group-hover/chip:opacity-100 group-focus-within/chip:opacity-100 motion-reduce:duration-[60ms]';

const Leading = ({ choice, look }: { choice: ChipChoice; look: ChipLook }) => {
  if (choice.avatar_url || look === 'chip') {
    return (
      <Avatar
        id={choice.value}
        name={choice.label}
        avatarUrl={choice.avatar_url}
        shape="circle"
        size={16}
        initials="one"
      />
    );
  }
  if (choice.color) {
    return (
      <span
        aria-hidden="true"
        className="size-[calc(8*var(--ac-u))] shrink-0 rounded-full"
        style={{ backgroundColor: choice.color }}
      />
    );
  }
  return null;
};

export const Chip = ({
  choice,
  look,
  added,
  invalidMessage,
  onRemove,
}: {
  choice: ChipChoice;
  look: ChipLook;
  added: boolean;
  // Why this entry fails its field's per-entry check, if it does
  invalidMessage: string | undefined;
  onRemove: (() => void) | undefined;
}) => {
  const messageId = useId();
  // The address sits behind the name
  const address =
    choice.detail ?? (choice.label !== choice.value ? choice.value : undefined);
  const content = (
    <>
      {look === 'text' ? null : <Leading choice={choice} look={look} />}
      <span className="min-w-0 truncate">{choice.label}</span>
    </>
  );
  return (
    <span
      // A tap focuses the chip, so on touch only that chip shows its remove button
      tabIndex={onRemove ? -1 : undefined}
      title={invalidMessage ?? address}
      aria-describedby={invalidMessage ? messageId : undefined}
      className={cn(
        'group/chip inline-flex min-w-0 max-w-full shrink-0 items-center whitespace-nowrap outline-none',
        LOOK_CLASS[look],
        added && ADDED_CLASS,
        invalidMessage && INVALID_CLASS
      )}
    >
      {look === 'pill' && onRemove ? (
        <span className={PILL_CONTENT_CLASS}>{content}</span>
      ) : (
        content
      )}
      {invalidMessage ? (
        <span id={messageId} className="sr-only">
          {invalidMessage}
        </span>
      ) : null}
      {onRemove ? (
        <button
          type="button"
          aria-label={`Remove ${choice.label}`}
          // Keeps focus in the row's input, so the row stays open
          onMouseDown={(event) => event.preventDefault()}
          onClick={onRemove}
          className={cn(REMOVE_CLASS, look === 'text' && TEXT_REMOVE_CLASS)}
        >
          <Icons.cancel className={ICON_SM_CLASS} aria-hidden="true" />
        </button>
      ) : look === 'chip' ? (
        <span
          aria-hidden="true"
          className="size-[calc(16*var(--ac-u))] shrink-0"
        />
      ) : null}
    </span>
  );
};
